import { mount } from 'svelte';
import App from './App.svelte';
import { getMembershipId, handleCallback, isAuthed, subscribeAuth } from '$api/auth';
import { migrateLocalBuildsToCloud, setCurrentMembership } from '$domain/repository';
import { cloudConfigured, whoami } from '$domain/cloud-builds';
import { setCurrentUser } from '$domain/session.svelte';
import { loadFlags } from '$domain/flags.svelte';
import { bootSettings } from '$domain/settings.svelte';
import { installErrorCapture, trackEvent } from '$api/telemetry';
import { dlog, isDebug, toggleDebug } from '$domain/debug';
import '$styles/reset.css';
import '$styles/tokens.css';
import '$styles/app.css';

// Build-time constants surface the deployed commit + date as a single
// string the issue capture can stamp onto error reports. Without this
// we can't tell which version actually shipped a regression.
const BUILD_HASH: string = __BUILD_HASH__;

// Apply persisted theme / density / accent BEFORE mounting Svelte so
// the first paint already matches the user's last choices — otherwise
// a dark-theme user on a default-light boot sees a flash of light.
bootSettings();

installErrorCapture(BUILD_HASH);
// Kick off the flag fetch the moment the script runs. It races the
// initial render — components that read flags get either the server
// value (fast path, edge-cached) or the code default during the brief
// window before the response lands. Either way they're correct.
void loadFlags();
// Single anonymous (or attributed-once-token-resolves) event per cold
// app load. Used as the primary "active app sessions" metric on the
// admin overview.
void trackEvent({ kind: 'app_open', path: window.location.pathname + window.location.hash });

const target = document.getElementById('app');
if (!target) throw new Error('cryptarch: #app missing');
target.innerHTML = '';

// One-time legacy cache eviction. Skip on OAuth callback URLs because
// unregistering the SW mid-callback restarts the page and races with the
// one-shot ?code= token exchange.
const CACHE_MARKER = 'cryptarch-cache-v15';
const isOAuthCallback = new URL(window.location.href).searchParams.has('code');
if (!isOAuthCallback && !localStorage.getItem(CACHE_MARKER)) {
  void (async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (err) {
      console.warn('[cache-evict]', err);
    } finally {
      localStorage.setItem(CACHE_MARKER, '1');
    }
  })();
}

// process ?code=... before the router reads the URL.
void handleCallback().catch((err: unknown) => {
  console.error('[auth]', err);
  // Surface to the UI via a CustomEvent so the next Inventory mount can show
  // a banner. Stashed in sessionStorage so it survives the SPA navigation that
  // happens immediately after handleCallback strips the search params.
  const msg = err instanceof Error ? err.message : 'Sign-in failed.';
  try {
    sessionStorage.setItem('cryptarch:authError', msg);
  } catch {
    // sessionStorage can be disabled (private mode in some browsers); silent fallback.
  }
  window.dispatchEvent(new CustomEvent('cryptarch:auth-error', { detail: msg }));
});

// Tie auth state to the builds repository so cloud builds light up the
// moment the user signs in (and switch back to IDB-only on logout).
// Migration of any pre-existing IDB builds runs once per (membership +
// browser) — idempotent thanks to the localStorage marker inside.
async function syncRepoToAuth(): Promise<void> {
  if (!cloudConfigured()) return;
  if (!(await isAuthed())) {
    setCurrentMembership(null);
    setCurrentUser(null);
    return;
  }
  const id = await getMembershipId();
  if (!id) {
    setCurrentMembership(null);
    setCurrentUser(null);
    return;
  }
  setCurrentMembership(id);
  try {
    // Touch the server-side user row + verify our token is good. If the
    // call 401s the user effectively has no cloud account and we silently
    // fall back to IDB (already handled by setCurrentMembership(null)).
    const me = await whoami();
    setCurrentUser({
      membershipId: me.membershipId,
      displayName: me.displayName,
      isAdmin: me.isAdmin,
    });
    const report = await migrateLocalBuildsToCloud(id);
    if (report.uploaded > 0) {
      dlog('app', `migrated ${report.uploaded} local build(s) to cloud`);
    }
  } catch (err) {
    console.warn('[cloud-sync]', err);
    setCurrentUser(null);
  }
}

subscribeAuth(() => {
  void syncRepoToAuth();
});
void syncRepoToAuth();

// Debug logger: hotkey toggle + boot announcement so a debug-on user
// sees confirmation in devtools the very first paint. Without this it's
// easy to think "I added ?debug=1, why am I not seeing logs?" when the
// answer is just that nothing has logged yet on that route.
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
      e.preventDefault();
      const now = toggleDebug();
      // Update the visible banner without a full re-render.
      const banner = document.getElementById('cryptarch-debug-banner');
      if (banner) banner.style.display = now ? 'block' : 'none';
    }
  });
  if (isDebug()) {
    dlog('app', `boot — build ${BUILD_HASH}`, () => ({
      ua: navigator.userAgent,
      lang: navigator.language,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }));
    const banner = document.getElementById('cryptarch-debug-banner');
    if (banner) banner.style.display = 'block';
  }
}

export default mount(App, { target });
